import { useState, useEffect } from "react";
import axios from "axios";
import {
  Cloud,
  Check,
  X,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  RefreshCw,
  ExternalLink,
  HardDrive,
  Upload,
  FolderSync,
  Settings,
  Database,
  ArrowRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OneDriveSettings() {
  const [config, setConfig] = useState({
    enabled: false,
    client_id: "",
    client_secret: "",
    tenant_id: "common",
    target_user_email: ""
  });
  const [status, setStatus] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [connectionError, setConnectionError] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
    fetchMigrationStatus();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/admin/onedrive-config`);
      setConfig({
        enabled: response.data.enabled || false,
        client_id: response.data.client_id || "",
        client_secret: "",
        tenant_id: response.data.tenant_id || "common",
        target_user_email: response.data.target_user_email || "",
        client_secret_configured: response.data.client_secret_configured,
        client_secret_masked: response.data.client_secret_masked
      });
    } catch (error) {
      console.error("Failed to fetch OneDrive config:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/onedrive-status`);
      setStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch OneDrive status:", error);
    }
  };

  const fetchMigrationStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/onedrive-migration-status`);
      setMigrationStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch migration status:", error);
    }
  };

  const saveConfig = async () => {
    if (!config.client_id) {
      toast.error("Client ID is required");
      return;
    }
    if (!config.client_secret && !config.client_secret_configured) {
      toast.error("Client Secret is required");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/admin/onedrive-config`, {
        enabled: config.enabled,
        client_id: config.client_id,
        client_secret: config.client_secret || undefined,
        tenant_id: config.tenant_id,
        target_user_email: config.target_user_email || undefined
      });
      toast.success("OneDrive configuration saved!");
      fetchConfig();
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    if (!config.client_id) {
      toast.error("Client ID is required");
      return;
    }
    if (!config.client_secret && !config.client_secret_configured) {
      toast.error("Client Secret is required");
      return;
    }
    if (!config.target_user_email) {
      toast.error("Target User Email is required for app authentication");
      return;
    }

    setTesting(true);
    setConnectionError(null);
    try {
      const response = await axios.post(`${API}/admin/onedrive-config/test`, {
        enabled: true,
        client_id: config.client_id,
        client_secret: config.client_secret || "use_existing",
        tenant_id: config.tenant_id,
        target_user_email: config.target_user_email
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setStatus({
          ...status,
          connected: true,
          owner: response.data.owner,
          ...response.data.storage
        });
        setConnectionError(null);
      } else {
        const errorMsg = response.data.error || "Connection failed";
        // Check for Conditional Access policy error
        if (errorMsg.includes("AADSTS53003") || errorMsg.includes("Conditional Access")) {
          setConnectionError({
            type: "conditional_access",
            message: "Azure AD Conditional Access policy is blocking this application.",
            details: "Your IT administrator needs to approve or whitelist this application in Azure AD."
          });
        } else if (errorMsg.includes("invalid_client") || errorMsg.includes("AADSTS7000215")) {
          setConnectionError({
            type: "invalid_credentials",
            message: "Invalid Client ID or Client Secret.",
            details: "Please verify your credentials in Azure Portal > App Registrations."
          });
        } else {
          setConnectionError({
            type: "general",
            message: "Connection failed",
            details: errorMsg
          });
        }
        toast.error("Connection test failed - see details below");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
      setConnectionError({
        type: "general",
        message: "Request failed",
        details: error.response?.data?.detail || error.message
      });
    } finally {
      setTesting(false);
    }
  };

  const startMigration = async () => {
    if (!status?.connected) {
      toast.error("Please connect to OneDrive first");
      return;
    }

    setMigrating(true);
    try {
      await axios.post(`${API}/admin/onedrive-migrate`);
      toast.success("Migration started! This may take a while for large datasets.");
      
      // Poll for progress
      const interval = setInterval(async () => {
        const response = await axios.get(`${API}/admin/onedrive-migration-status`);
        setMigrationStatus(response.data);
        
        if (response.data.pending === 0) {
          clearInterval(interval);
          setMigrating(false);
          toast.success("Migration complete!");
        }
      }, 5000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start migration");
      setMigrating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="onedrive-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Cloud className="w-8 h-8 text-[#0078D4]" />
          OneDrive Storage
        </h1>
        <p className="text-[#5C5C5C] mt-1">
          Store presentations and recordings in Microsoft OneDrive for Business
        </p>
      </div>

      {/* Status Banner */}
      <Card className={`${status?.connected ? 'bg-gradient-to-r from-[#0078D4] to-[#106EBE]' : 'bg-gradient-to-r from-slate-600 to-slate-700'} text-white border-0`}>
        <CardContent className="py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center">
                <Cloud className="w-8 h-8" />
              </div>
              <div>
                <p className="text-sm opacity-80">Storage Provider</p>
                <h2 className="text-2xl font-bold">OneDrive for Business</h2>
                {status?.owner && (
                  <p className="text-sm opacity-80 mt-1">Owner: {status.owner}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              {status?.connected ? (
                <>
                  <div className="flex items-center gap-2 text-green-300">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Connected</span>
                  </div>
                  {status.quota_used_gb !== undefined && (
                    <p className="text-sm opacity-80 mt-1">
                      {status.quota_used_gb} GB / {status.quota_total_gb} GB used
                    </p>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-2 text-yellow-300">
                  <AlertCircle className="w-5 h-5" />
                  <span className="font-medium">Not Connected</span>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Configuration */}
        <Card className="stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-primary" />
              Configuration
            </CardTitle>
            <CardDescription>
              Azure AD App Registration credentials
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Enable toggle */}
            <div className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl">
              <div>
                <Label className="font-medium">Enable OneDrive Storage</Label>
                <p className="text-sm text-[#5C5C5C]">Store files in OneDrive instead of MongoDB</p>
              </div>
              <Switch
                checked={config.enabled}
                onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
                data-testid="toggle-onedrive"
              />
            </div>

            {/* Client ID */}
            <div className="space-y-2">
              <Label htmlFor="client-id">Application (Client) ID</Label>
              <Input
                id="client-id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.client_id}
                onChange={(e) => setConfig({ ...config, client_id: e.target.value })}
                data-testid="input-client-id"
              />
            </div>

            {/* Tenant ID */}
            <div className="space-y-2">
              <Label htmlFor="tenant-id">Directory (Tenant) ID</Label>
              <Input
                id="tenant-id"
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                value={config.tenant_id}
                onChange={(e) => setConfig({ ...config, tenant_id: e.target.value })}
              />
              <p className="text-xs text-[#5C5C5C]">Your Azure AD tenant ID (required for app authentication)</p>
            </div>

            {/* Target User Email */}
            <div className="space-y-2">
              <Label htmlFor="target-user-email">Target User Email <span className="text-red-500">*</span></Label>
              <Input
                id="target-user-email"
                type="email"
                placeholder="user@yourcompany.com"
                value={config.target_user_email}
                onChange={(e) => setConfig({ ...config, target_user_email: e.target.value })}
                data-testid="input-target-email"
              />
              <p className="text-xs text-[#5C5C5C]">The email of the user whose OneDrive will store the files</p>
            </div>

            {/* Client Secret */}
            <div className="space-y-2">
              <Label htmlFor="client-secret">Client Secret</Label>
              <div className="relative">
                <Input
                  id="client-secret"
                  type={showSecret ? "text" : "password"}
                  placeholder={config.client_secret_configured ? "••••••••••••" : "Enter client secret"}
                  value={config.client_secret}
                  onChange={(e) => setConfig({ ...config, client_secret: e.target.value })}
                  className="pr-10"
                  data-testid="input-client-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#5C5C5C]"
                >
                  {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {config.client_secret_masked && !config.client_secret && (
                <p className="text-xs text-[#5C5C5C]">Current: {config.client_secret_masked}</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={testConnection}
                disabled={testing}
                className="rounded-full"
                data-testid="test-onedrive"
              >
                {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Test Connection
              </Button>
              <Button
                onClick={saveConfig}
                disabled={saving}
                className="rounded-full"
                data-testid="save-onedrive"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>

            {/* Connection Error Display */}
            {connectionError && (
              <div className={`p-4 rounded-xl border ${
                connectionError.type === 'conditional_access' 
                  ? 'bg-amber-50 border-amber-200' 
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${
                    connectionError.type === 'conditional_access' 
                      ? 'text-amber-600' 
                      : 'text-red-600'
                  }`} />
                  <div>
                    <h4 className={`font-medium ${
                      connectionError.type === 'conditional_access' 
                        ? 'text-amber-800' 
                        : 'text-red-800'
                    }`}>
                      {connectionError.message}
                    </h4>
                    <p className={`text-sm mt-1 ${
                      connectionError.type === 'conditional_access' 
                        ? 'text-amber-700' 
                        : 'text-red-700'
                    }`}>
                      {connectionError.details}
                    </p>
                    {connectionError.type === 'conditional_access' && (
                      <div className="mt-3 p-3 bg-white/50 rounded-lg">
                        <p className="text-xs text-amber-800 font-medium">Steps to resolve:</p>
                        <ol className="text-xs text-amber-700 mt-1 list-decimal list-inside space-y-1">
                          <li>Contact your IT/Azure administrator</li>
                          <li>Request approval for the Conditional Access policy</li>
                          <li>Or ask them to whitelist this application</li>
                        </ol>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Help Link */}
            <div className="pt-4 border-t">
              <a
                href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <ExternalLink className="w-3 h-3" />
                Azure Portal - App Registrations
              </a>
              <p className="text-xs text-[#5C5C5C] mt-1">
                Required API permissions: Files.ReadWrite.All, Sites.ReadWrite.All
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Migration */}
        <Card className="stats-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FolderSync className="w-5 h-5 text-primary" />
              Data Migration
            </CardTitle>
            <CardDescription>
              Move existing files from MongoDB to OneDrive
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Migration Status */}
            {migrationStatus && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-secondary/30 rounded-xl">
                    <Database className="w-6 h-6 mx-auto mb-2 text-[#5C5C5C]" />
                    <p className="text-2xl font-bold">{migrationStatus.total_with_files}</p>
                    <p className="text-xs text-[#5C5C5C]">Total Sessions</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-xl">
                    <Cloud className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <p className="text-2xl font-bold text-green-600">{migrationStatus.migrated}</p>
                    <p className="text-xs text-[#5C5C5C]">On OneDrive</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-xl">
                    <HardDrive className="w-6 h-6 mx-auto mb-2 text-orange-600" />
                    <p className="text-2xl font-bold text-orange-600">{migrationStatus.pending}</p>
                    <p className="text-xs text-[#5C5C5C]">Pending</p>
                  </div>
                </div>

                {/* Progress bar */}
                {migrationStatus.total_with_files > 0 && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Migration Progress</span>
                      <span className="font-medium">{migrationStatus.percent_complete}%</span>
                    </div>
                    <Progress value={migrationStatus.percent_complete} className="h-2" />
                  </div>
                )}
              </div>
            )}

            {/* Migration Info */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">Storage Structure</h4>
              <div className="text-sm text-blue-700 space-y-1 font-mono">
                <p>📁 EVOLVE/</p>
                <p className="ml-4">📁 Sessions/</p>
                <p className="ml-8">📁 {`{session_id}`}/</p>
                <p className="ml-12">📁 slides/</p>
                <p className="ml-12">📁 presentations/</p>
                <p className="ml-12">📁 recordings/</p>
              </div>
            </div>

            {/* Migration Benefits */}
            <div className="space-y-2">
              <h4 className="font-medium text-sm">Benefits of OneDrive Storage:</h4>
              <ul className="text-sm text-[#5C5C5C] space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Reduces MongoDB storage load
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Files persist across deployments
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Direct URLs for faster loading
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  Leverages Microsoft 365 storage
                </li>
              </ul>
            </div>

            {/* Start Migration Button */}
            <Button
              onClick={startMigration}
              disabled={!status?.connected || migrating || (migrationStatus?.pending === 0)}
              className="w-full rounded-full"
              data-testid="start-migration"
            >
              {migrating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Migrating...
                </>
              ) : migrationStatus?.pending === 0 ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  All Files Migrated
                </>
              ) : (
                <>
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Start Migration ({migrationStatus?.pending || 0} files)
                </>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
